import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, FormControl, MenuItem, OutlinedInput, Select } from '@mui/material';
import { ModalClose } from '../assets/icons';
import TickIcon from '../assets/icons/mdiTickIcon';
import { toast } from 'react-toastify';
import AuthService from '../services/auth.service';
import { decodeMetadataToken } from '../utils/token.utils';

interface UpdateUserDomainProps {
    open: boolean;
    onClose: () => void;
    domain: string;
    onConfirm: (newDomain: string) => void;
    subdomain?: string;
    customDomain?: string;
    customDomainStatus?: string;
    companyId?: string;
    cognitoUserId?: string;
}

const UpdateUserDomainModal: React.FC<UpdateUserDomainProps> = ({ 
    open, 
    onClose, 
    onConfirm, 
    domain, 
    subdomain,
    customDomain,
    customDomainStatus,
    companyId,
    cognitoUserId
}) => {
    const [customDomainValue, setCustomDomainValue] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState("ACTIVE")

    useEffect(() => {
        if (open) {
            setCustomDomainValue(customDomain || '');
            setStatus(customDomainStatus || 'ACTIVE');
            setError('');
        }
    }, [open, customDomain, customDomainStatus]);

    const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCustomDomainValue(value);

        // Validation
        if (!value) {
            setError('Domain name is required');
        } else if (!/^[a-zA-Z0-9-.]+$/.test(value)) {
            setError('Only letters, numbers, hyphens, and periods are allowed');
        } else {
            setError('');
        }
    };

    const handleConfirm = async () => {
        if (!error && customDomainValue) {
            setIsLoading(true);
            let targetCompanyId = companyId || '';
            
            // Don't proceed if no company ID is available
            if (!targetCompanyId) {
                toast.error("No company ID available");
                setIsLoading(false);
                return;
            }
            
            try {
                const response = await AuthService.updateCompanyCustomDomain(
                    targetCompanyId,
                    customDomainValue,
                    status,
                    cognitoUserId || '',
                    subdomain || ''
                );

                console.log(response);

                if (response && response.data) {
                    toast.success('Custom domain updated successfully');
                    onConfirm(customDomainValue);
                    onClose();
                } else {
                    toast.error('Failed to update custom domain');
                }
            } catch (error: any) {
                console.log(error);
                toast.error(error.message || 'An error occurred while updating custom domain');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleStatusChange = (e: any) => {
        setStatus(e.target.value)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className='card-details-modal'
            sx={{ '& .MuiDialog-paper': { width: '661px', maxWidth: '661PX', borderRadius: '24px', border: '1px solid #E8E8EA' } }}
        >
            <DialogTitle sx={{ m: 0, }} p={{ xs: "0 16px 16px", sm: "0 32px 32px" }}>
                <Typography color='#333333' fontSize={{ xs: "22px", sm: "28px" }} fontWeight='500'>Edit Domain</Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={() => ({
                        position: 'absolute',
                        right: { xs: "17px" },
                        top: { xs: "15px" },
                    })}
                >
                    <ModalClose />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "0 16px 16px", sm: "0 32px 32px" } }}>
                <Box bgcolor="#DFEBFF" borderRadius="8px" padding="12px 10px" mb="40px">
                    <Typography component="span" fontWeight="600" color="#2B2B30" fontSize="16px">
                        Note:<Typography component="span" ml="4px">Only letters, numbers, hyphens, and periods are allowed in domain name. Do not add www.</Typography>
                    </Typography>
                </Box>
                <Typography variant="body1" color="#333333" mb="10px" sx={{ opacity: '0.6' }}>Sub Domain</Typography>
                <Box display="flex" alignItems="center">
                    <Typography color="#333" fontSize="16px" fontWeight="500" mr="12px">https://</Typography>
                    <TextField
                        variant="outlined"
                        placeholder='your-domain'
                        value={subdomain }
                        disabled
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: '8px',
                                borderColor: '#C7C7C7',
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: '#333333',
                                border: '1px solid'
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: '14px 16px',
                            },
                            "&.MuiInputBase-input::placeholder": {
                                color: '#333333',
                                fontSize: '16px',
                                opacity: '0.8'
                            },
                            "& .MuiFormHelperText-root": {
                                color: '#E44258',
                                marginLeft: 0,
                                marginTop: '8px'
                            }
                        }}
                        fullWidth
                    />
                    <Box display="flex">
                        <Typography color="#333" fontSize="16px" fontWeight="500" margin="0 12px">{domain}</Typography>
                        <TickIcon />
                    </Box>
                </Box>
                <Typography variant="body1" color="#333333" mb="10px" mt="16px" sx={{ opacity: '0.6' }}>Custom Domain</Typography>
                <Box display="flex" alignItems="center" width="100%">
                    <Typography color="#333" fontSize="16px" fontWeight="500" mr="12px">https://</Typography>
                    <Box display="flex" alignItems="center" width="100%" maxWidth="378px">
                        <TextField
                            variant="outlined"
                            placeholder='your-domain'
                            value={customDomainValue}
                            onChange={handleDomainChange}
                            error={!!error}
                            helperText={error}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: '8px',
                                    borderColor: '#C7C7C7',
                                    maxWidth: "381px",
                                    width: "100%",
                                },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: '#333333',
                                    border: '1px solid'
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: '14px 16px',
                                },
                                "&.MuiInputBase-input::placeholder": {
                                    color: '#333333',
                                    fontSize: '16px',
                                    opacity: '0.8'
                                },
                                "& .MuiFormHelperText-root": {
                                    color: '#E44258',
                                    marginLeft: 0,
                                    marginTop: '8px'
                                }
                            }}
                        />
                        <Box ml={1.5}>
                            {!error && <TickIcon />}
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" width="100%" mt="16px">
                    <Typography color="#333" fontSize="16px" fontWeight="500" mr="19px">Status</Typography>
                    <FormControl sx={{ width: "100%", maxWidth: "342px" }}>
                        <Select
                            value={status}
                            onChange={handleStatusChange}
                            input={<OutlinedInput />}
                            displayEmpty
                            sx={{
                                borderRadius: "8px",
                                "& .MuiInputBase-input > div:hover": {
                                    background: "transparent",
                                },
                                "& .MuiInputBase-input": {
                                    padding: '14px 16px',
                                    "> div": { p: "0" }
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: "12px",
                                        mt: 1,
                                        maxWidth: "300px",
                                        "& .MuiMenu-list": {
                                            padding: "8px",
                                        },
                                        "& .MuiMenuItem-root": {
                                            padding: 0,
                                            borderRadius: "12px",
                                            "&:hover": {
                                                backgroundColor: "transparent"
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            <MenuItem key="active" value="ACTIVE" sx={{
                                py: "0", ":hover": { background: "unset" }, "&.MuiPaper-root": {
                                    borderRadius: "12px"
                                },
                                "&.Mui-selected": { "& p": { color: (theme) => theme.palette.primary.main } }
                            }}>
                                <Box width="100%" p="10px" sx={{
                                    ":hover": {
                                        backgroundColor: "#DFEBFF",
                                        borderRadius: "12px",
                                    }
                                }}>
                                    <Typography mb="3px" color="#333" fontSize="14px" fontWeight="500">
                                        ACTIVE
                                    </Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem key="inactive" value="INACTIVE" sx={{
                                py: "0", ":hover": { background: "unset" }, "&.MuiPaper-root": {
                                    borderRadius: "12px"
                                },
                                "&.Mui-selected": { "& p": { color: (theme) => theme.palette.primary.main } }
                            }}>
                                <Box width="100%" p="10px" sx={{
                                    ":hover": {
                                        backgroundColor: "#DFEBFF",
                                        borderRadius: "12px",
                                    }
                                }}>
                                    <Typography mb="3px" color="#333" fontSize="14px" fontWeight="500">
                                        INACTIVE
                                    </Typography>
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" } }}>
                <Button
                    onClick={onClose}
                    color="primary"
                    disabled={isLoading}
                    sx={{
                        border: '1px solid #E8EAF0',
                        px: '22px',
                        color: '#2B2B30 !important',
                        textTransform: 'capitalize',
                        borderRadius: '8px',
                        mr: '16px',
                        '&:hover': {
                            border: '1px solid #E8EAF0 !important',
                            boxShadow: 'unset !important'
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!!error || !customDomainValue || isLoading}
                    variant="contained"
                    size='large'
                    sx={{
                        borderRadius: '8px',
                        '&.Mui-disabled': {
                            bgcolor: '#E8EAF0',
                            color: '#8F8F8F !important'
                        }
                    }}
                >
                    {isLoading ? 'Updating...' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateUserDomainModal;
